import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '@app/environments/environment';
import mapToData from '@app/helpers/rxjs/map-to-data.helper';
import {
  ATTENDANCE_DEVICE_TYPE_FOR_AUDIT,
  TerminalV2CheckUserResponse,
  TerminalV2ResponseData,
  TerminalV2UserClockInOutResponse,
} from '@app/models/api/terminal-v2/terminal-v2.model';
import { V1AuthTerminal } from '@app/models/api/v1/auth/auth.model';
import Nullable from '@app/models/typescript/nullable.model';

@Injectable({
  providedIn: 'root',
})
export class TerminalV2Service {
  private readonly http = inject(HttpClient);

  public checkTerminalV2UserActionByCardNumber(cardNumber: string): Observable<TerminalV2CheckUserResponse> {
    const params = new HttpParams().set('card_id', cardNumber);
    return this.http
      .get<TerminalV2CheckUserResponse>(`${environment.backendUrl}v1/module-attendance-v2/terminal/user-by-card`, { params })
      .pipe(mapToData());
  }

  public unlockTerminalV2(lockPinCode: string): Observable<V1AuthTerminal> {
    return this.http
      .put<V1AuthTerminal>(`${environment.backendUrl}v1/module-attendance-v2/terminal/unlock`, { lock_pin_code: lockPinCode })
      .pipe(mapToData());
  }

  public lockTerminalV2(terminalUuid: V1AuthTerminal['uuid'], lockPinCode: string): Observable<void> {
    return this.http.put<void>(`${environment.backendUrl}v1/module-attendance-v2/terminal/${terminalUuid}/lock`, {
      lock_pin_code: lockPinCode,
    });
  }

  public activateTerminalV2(terminalLoginCode: string): Observable<TerminalV2ResponseData> {
    return this.http
      .put<TerminalV2ResponseData>(`${environment.backendUrl}v1/module-attendance-v2/terminal/${terminalLoginCode}/activate`, {})
      .pipe(mapToData());
  }

  public terminalV2UserClockInAction(userUuid: string, terminalUuid: string, photo?: Blob): Observable<TerminalV2UserClockInOutResponse> {
    return this.http
      .post<TerminalV2UserClockInOutResponse>(
        `${environment.backendUrl}v1/module-attendance-v2/attendance-segment-event`,
        this.createFormDataForClockInOut(userUuid, terminalUuid, undefined, ATTENDANCE_DEVICE_TYPE_FOR_AUDIT.TERMINAL, photo),
      )
      .pipe(mapToData());
  }

  public terminalV2UserEndOfShift(userUuid: string, photo?: Blob): Observable<TerminalV2UserClockInOutResponse> {
    return this.http
      .post<TerminalV2UserClockInOutResponse>(
        `${environment.backendUrl}v1/module-attendance-v2/attendance-segment-event/close`,
        this.createFormDataForClockInOut(userUuid, undefined, undefined, undefined, photo),
      )
      .pipe(mapToData());
  }

  public terminalV2UserClockOutWithOption(
    userUuid: string,
    terminalUuid: Nullable<string>,
    breakTypeUuid: string,
    photo?: Blob,
  ): Observable<TerminalV2UserClockInOutResponse> {
    return this.http
      .post<TerminalV2UserClockInOutResponse>(
        `${environment.backendUrl}v1/module-attendance-v2/attendance-segment-event`,
        this.createFormDataForClockInOut(userUuid, terminalUuid, breakTypeUuid, ATTENDANCE_DEVICE_TYPE_FOR_AUDIT.TERMINAL, photo),
      )
      .pipe(mapToData());
  }

  private createFormDataForClockInOut(
    userUuid: string,
    terminalUuid: Nullable<string>,
    breakTypeUuid: Nullable<string>,
    deviceType?: ATTENDANCE_DEVICE_TYPE_FOR_AUDIT,
    photo?: Blob,
  ): FormData {
    const formData: FormData = new FormData();
    formData.append('user_uuid', userUuid);
    if (terminalUuid) {
      formData.append('terminal_uuid', terminalUuid);
    }
    if (deviceType) {
      formData.append('device_type', deviceType);
    }
    if (breakTypeUuid) {
      formData.append('attendance_segment_event_break_type_uuid', breakTypeUuid);
    }
    if (photo) {
      formData.append('terminal_photo', photo);
    }
    return formData;
  }
}
